import { JSONSchemaType } from "ajv";
import { colord, extend } from "colord";
import type { Colord } from "colord";
import mixPlugin from "colord/plugins/mix";
import { defineStore } from "pinia";
import { useKeyboardStore } from "@/stores";
import { LocalStorage } from "@/localStorage";

extend([mixPlugin]);

const DAISYUI_COLOR_VARS = {
  primary: "--p",
  "primary-focus": "--pf",
  "primary-content": "--pc",
  secondary: "--s",
  "secondary-focus": "--sf",
  "secondary-content": "--sc",
  accent: "--a",
  "accent-focus": "--af",
  "accent-content": "--ac",
  neutral: "--n",
  "neutral-focus": "--nf",
  "neutral-content": "--nc",
  "base-100": "--b1",
  "base-200": "--b2",
  "base-300": "--b3",
  "base-content": "--bc",
  info: "--in",
  "info-content": "--inc",
  success: "--su",
  "success-content": "--suc",
  warning: "--wa",
  "warning-content": "--wac",
  error: "--er",
  "error-content": "--erc",
} as const;

type DaisyUiColor = keyof typeof DAISYUI_COLOR_VARS;

const daisyUiColor = (c: DaisyUiColor): Colord => {
  const cssVar = DAISYUI_COLOR_VARS[c];
  const hsl = getComputedStyle(document.body).getPropertyValue(cssVar);
  return colord(`hsl(${hsl})`);
};

const DAISYUI_COLORS: Record<DaisyUiColor, Colord> = Object.keys(
  DAISYUI_COLOR_VARS
).reduce((acc, color) => {
  const c = color as DaisyUiColor;
  acc[c] = daisyUiColor(c);
  return acc;
}, {} as Record<DaisyUiColor, Colord>);

type ColorSet = {
  base: Colord;
  focus: Colord;
  content: Colord;
};

// Don't use colord.darken() as it operates on `ratio * 100`, while color (daisyui)
// operates on current value of lightness (hsl.color[2]).
const darken = (c: Colord, ratio: number): Colord => {
  const hsl = c.toHsl();
  hsl.l -= hsl.l * ratio;
  return colord(hsl);
};
const isDark = (c: Colord): boolean => {
  const yiq = (c.rgba.r * 2126 + c.rgba.g * 7152 + c.rgba.b * 722) / 10000;
  return yiq < 128;
};

const WHITE = colord("#fff");
const BLACK = colord("#000");

export const generateColorSet = (base: Colord): ColorSet => {
  const focus = darken(base, 0.2);
  const content = base
    .mix(isDark(base) ? WHITE : BLACK, 0.8)
    .saturate(100 /* to 100% */);
  return { base, focus, content };
};

const randInt = (max: number) => Math.floor(Math.random() * max);
const randSelect = <T>(list: T[]): T =>
  list[Math.min(list.length - 1, randInt(list.length))];

function randomize<T>(keys: T[][], candidates: T[], chance?: number) {
  keys.forEach((row) =>
    row.forEach((_, c) => {
      if ((chance ?? 1.0) > Math.random()) {
        row[c] = randSelect(candidates);
      }
    })
  );
}

const generateRandom = (): Colord[][] => {
  const kb = useKeyboardStore();
  const colors: DaisyUiColor[][] = Array(kb.rows)
    .fill(undefined)
    .map(() => Array(kb.columns).fill("primary"));

  randomize(
    colors,
    [
      "primary",
      "primary",
      "primary",
      "primary",
      "primary",
      "primary",
      "neutral",
      "neutral",
      "neutral",
      "secondary",
      "secondary",
      "accent",
    ],
    0.5
  );

  return colors.map((row) => row.map((c) => daisyUiColor(c)));
};

const DEFAULT_COLOR_NAMES: DaisyUiColor[] = [
  "primary",
  "neutral",
  "secondary",
  "accent",
];

const getStorage = () => {
  const kb = useKeyboardStore();
  const schema: JSONSchemaType<string[][]> = {
    type: "array",
    minItems: kb.rows,
    maxItems: kb.rows,
    items: {
      type: "array",
      minItems: kb.columns,
      maxItems: kb.columns,
      items: {
        type: "string",
      },
    },
  };
  const serialize = (v: Colord[][]): string[][] | undefined =>
    v.map((row) => row.map((col) => col.toHex()));
  const deserialize = (v: string[][]): Colord[][] | undefined => {
    const parsed = v.map((row) => row.map(colord));
    if (!parsed.flat().every((c) => c.isValid())) return;
    return parsed;
  };
  return new LocalStorage("keycap-colors", schema, serialize, deserialize);
};

export const useKeycapColorsStore = defineStore("colors", {
  state: () => {
    const storage = getStorage();
    const loaded = storage.get();

    const status = loaded == undefined ? "could not load" : "loaded";
    // console.log(`keycap colors: ${status} from local storage`);

    const colors = loaded ?? generateRandom();
    storage.set(colors);

    // Cannot be global const because it must be calculated after CSS is loaded
    const defaultColors = DEFAULT_COLOR_NAMES.map((c) => daisyUiColor(c));

    return {
      colors,
      saveTimerId: null as null | number,
      saveDelay: 500,
      defaultColors,
    };
  },

  getters: {
    at(state): (row: number, col: number) => Colord {
      return (row, col) =>
        colord((state.colors?.[row]?.[col] ?? DAISYUI_COLORS.primary).rgba);
    },
  },

  actions: {
    triggerSave() {
      if (this.saveTimerId != null) clearTimeout(this.saveTimerId);
      this.saveTimerId = setTimeout(() => {
        const storage = getStorage();
        storage.set(this.colors as Colord[][]);
      }, this.saveDelay);
    },

    setRandom(at?: [number, number][]) {
      const colors = generateRandom();
      this.colors =
        at == undefined
          ? colors
          : (this.colors as Colord[][]).map((crow, row) =>
              crow.map((color, col) => {
                if (at.find(([r, c]) => r == row && c == col)) {
                  return colors[row][col];
                } else {
                  return color;
                }
              })
            );
      getStorage().set(this.colors as Colord[][]);
    },

    setRow(row: number, color: Colord) {
      const r = this.colors[row];
      if (r == undefined) return;
      r.fill(color);
      this.triggerSave();
    },

    setCol(col: number, color: Colord) {
      this.colors.forEach((row) =>
        row.forEach((_, c) => {
          if (c == col) row[c] = color;
        })
      );
      this.triggerSave();
    },

    setKey(row: number, col: number, color: Colord) {
      const r = this.colors[row];
      if (r == undefined) return;
      if (col < r.length) r[col] = color;
      this.triggerSave();
    },
  },
});
